<template>
  <div>
    <h3 class="h2">Configure your Stripe API keys</h3>
    <div>
      <br/>
       To copy paste your API keys, head over to the <a href="https://stripe.com/docs/keys" target="_blank">Stripe documentation page</a>, which provides you with guidelines on how to find them in your Stripe account.
    </div>
    <div class="row mt-4">
      <form-group class="col-xs-12" label="Stripe Secret">
        <ds-input v-model="stripe.stripe_secret" type="password" placeholder="sk_" />
      </form-group>
      <form-group class="col-xs-12" label="Stripe Publishable Key">
        <ds-input v-model="stripe.stripe_key" placeholder="pk_" />
      </form-group>
      <form-group class="col-xs-12" label="Stripe Webhook Secret">
        <ds-input v-model="stripe.webhook_secret" placeholder="whsec_" />
      </form-group>
    </div>

    <template v-if="stripeKeysConfigured">
      <!-- Preflight check -->
      <hr class="mt-4 mb-4">

      <h3 class="h2 mt-4">Configuration Status</h3>
      <p>Run a preflight check to verify all Stripe settings are in order. Use this after entering your keys, setting up the TRP product, and registering the webhook endpoint in Stripe.</p>

      <div v-if="preflightResult" style="margin-bottom: 16px;">
        <table style="width: 100%; border-collapse: collapse; margin-bottom: 12px;">
          <tbody>
            <tr v-for="item in preflightItems" :key="item.key" style="border-bottom: 1px solid #eee;">
              <td style="padding: 8px 4px; width: 28px; font-size: 16px;">
                {{ preflightResult.checks[item.key] ? '✅' : '❌' }}
              </td>
              <td style="padding: 8px 4px; font-weight: 500;">{{ item.label }}</td>
              <td style="padding: 8px 4px; color: #888; font-size: 13px;">{{ item.hint }}</td>
            </tr>
          </tbody>
        </table>

        <div v-if="preflightResult.connection_error" class="alert alert-warning" style="margin-bottom: 12px;">
          <strong>Connection error:</strong> {{ preflightResult.connection_error }}
        </div>

        <div class="alert alert-info" style="margin-bottom: 12px;">
          <strong>Webhook URL</strong> — register this in the Stripe Dashboard (Developers → Webhooks):<br>
          <code style="display: block; margin-top: 6px; word-break: break-all;">{{ preflightResult.webhook_url }}</code>
          <div style="margin-top: 10px;">
            <strong>Required webhook events:</strong>
            <ul style="margin: 4px 0 0 0; padding-left: 20px;">
              <li v-for="event in preflightResult.required_events" :key="event" style="font-size: 13px; font-family: monospace;">{{ event }}</li>
            </ul>
          </div>
        </div>
      </div>

      <div v-if="preflightError" class="alert alert-danger" style="margin-bottom: 12px;">
        {{ preflightError }}
      </div>

      <ds-button
        variant="secondary"
        label="Run Preflight Check"
        :disabled="preflightLoading"
        @click="runPreflight"
      />
      <span v-if="preflightResult && allChecksPass" style="margin-left: 12px; color: green; font-weight: 600;">All checks passed ✅</span>

      <hr class="mt-4 mb-4">
      <h3 class="h2">Transition Reports Package</h3>
      <p>Set up the Transition Reports Package product and yearly pricing in Stripe. Once created, you can update the price — existing subscribers are grandfathered on their current price, only new subscriptions use the updated price.</p>

      <div v-if="productAlreadySetUp" class="alert alert-success">
        <strong>Product configured</strong><br>
        Stripe Product ID: <code>{{ stripe.stripe_transition_reports_product_id }}</code><br>
        Stripe Price ID: <code>{{ stripe.stripe_transition_reports_price_id }}</code>
      </div>

      <!-- Subscriber breakdown per price — only shown when there are active subscribers -->
      <div v-if="subscribersByPrice.length > 0" style="margin: 16px 0; padding: 12px; background: #f8f9fa; border-radius: 4px; border: 1px solid #dee2e6;">
        <strong style="font-size: 13px;">Active subscribers by price plan:</strong>
        <table style="width: 100%; margin-top: 8px; border-collapse: collapse;">
          <thead>
            <tr style="border-bottom: 1px solid #dee2e6; text-align: left;">
              <th style="padding: 4px 8px; font-size: 12px; font-weight: 600;">Price ID</th>
              <th style="padding: 4px 8px; font-size: 12px; font-weight: 600;">Subscribers</th>
              <th style="padding: 4px 8px; font-size: 12px; font-weight: 600;">Status</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in subscribersByPrice" :key="item.price_id" style="border-bottom: 1px solid #f0f0f0;">
              <td style="padding: 4px 8px; font-size: 12px; font-family: monospace;">{{ item.price_id }}</td>
              <td style="padding: 4px 8px; font-size: 12px;">{{ item.count }}</td>
              <td style="padding: 4px 8px; font-size: 12px;">
                <span v-if="item.price_id === stripe.stripe_transition_reports_price_id" style="color: #28a745;">● Active</span>
                <span v-else style="color: #6c757d;">● Grandfathered</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="row mt-4">
        <form-group class="col-xs-6" label="Yearly price (€)">
          <ds-input v-model.number="yearlyPrice" type="number" placeholder="1200" />
        </form-group>
      </div>

      <div v-if="isPriceChange && grandfatheredCount > 0" class="alert alert-warning" style="margin-bottom: 12px;">
        <strong>{{ grandfatheredCount }} subscriber(s)</strong> are on a grandfathered price and will not be affected. Only new subscriptions and trials will use the updated price.
      </div>

      <div v-if="productError" class="alert alert-danger" style="margin-bottom: 12px;">
        {{ productError }}
      </div>

      <ds-button
        variant="secondary"
        :label="productButtonLabel"
        :disabled="productLoading || !yearlyPrice"
        @click="setupProduct"
      />
      <span v-if="productSuccess" style="margin-left: 12px; color: green;">{{ productSuccess }}</span>
    </template>
  </div>
</template>

<script lang="ts">
  import { createJSONRequest } from '@/api/create-request'

  export default {
    name: 'Stripe.vue',
    data () {
      return {
        yearlyPrice: null,
        productLoading: false,
        productError: null,
        productSuccess: null,
        preflightLoading: false,
        preflightResult: null,
        preflightError: null,
        preflightItems: [
          { key: 'secret_key',      label: 'Stripe Secret Key',    hint: 'sk_live_... or sk_test_...' },
          { key: 'publishable_key', label: 'Stripe Publishable Key', hint: 'pk_live_... or pk_test_...' },
          { key: 'webhook_secret',  label: 'Webhook Signing Secret', hint: 'whsec_... — copy from Stripe Dashboard webhook endpoint' },
          { key: 'product',         label: 'TRP Product configured', hint: 'Run "Set up Transition Reports Package" below' },
          { key: 'price',           label: 'TRP Price configured',   hint: 'Created automatically with the product' },
          { key: 'connection',      label: 'Live connection to Stripe', hint: 'Verified by a test API call using your secret key' },
        ],
      }
    },
    computed: {
      stripe () {
        return this.$parent.$parent.$parent.form.stripe
      },
      stripeKeysConfigured () {
        return !!(this.stripe && this.stripe.stripe_secret && this.stripe.stripe_secret.length > 0)
      },
      productAlreadySetUp () {
        return !!(this.stripe && this.stripe.stripe_transition_reports_product_id)
      },
      allChecksPass () {
        if (!this.preflightResult) return false
        return Object.values(this.preflightResult.checks).every(Boolean)
      },
      subscribersByPrice () {
        const list: { price_id: string; count: number }[] = this.preflightResult?.subscribers_by_price ?? []
        const activePriceId: string | undefined = this.stripe?.stripe_transition_reports_price_id
        // Always include the active price even if it has 0 subscribers
        if (activePriceId && !list.find(item => item.price_id === activePriceId)) {
          return [{ price_id: activePriceId, count: 0 }, ...list]
        }
        return list
      },
      // True when the product is already set up and the entered price differs from the active Stripe price
      isPriceChange () {
        return this.productAlreadySetUp
          && this.yearlyPrice
          && this.preflightResult?.price_amount_eur
          && this.yearlyPrice !== this.preflightResult.price_amount_eur
      },
      // Number of subscribers on grandfathered (non-active) price plans
      grandfatheredCount () {
        const activePriceId = this.stripe?.stripe_transition_reports_price_id
        return this.subscribersByPrice
          .filter((item: { price_id: string; count: number }) => item.price_id !== activePriceId)
          .reduce((sum: number, item: { price_id: string; count: number }) => sum + item.count, 0)
      },
      productButtonLabel () {
        if (!this.productAlreadySetUp) return 'Set up Transition Reports Package in Stripe'
        if (this.isPriceChange) return 'Update TRP Price in Stripe'
        return 'Sync TRP Product'
      },
    },
    created () {
      if (! this.$parent.$parent.$parent.form.stripe) {
        this.$parent.$parent.$parent.form.stripe = {
          'stripe_secret': '',
          'stripe_key': '',
          'webhook_secret': '',
        }
      } else if (this.$parent.$parent.$parent.form.stripe.webhook_secret === undefined) {
        this.$parent.$parent.$parent.form.stripe.webhook_secret = ''
      }
    },
    async mounted () {
      // If the TRP product is already configured, silently fetch the current price
      // so the yearly price input is pre-filled with the active Stripe price.
      if (this.productAlreadySetUp && this.stripeKeysConfigured) {
        try {
          const result = await createJSONRequest({ url: '/api/settings/stripe/preflight', method: 'GET' })
          this.preflightResult = result
          if (result.price_amount_eur) {
            this.yearlyPrice = result.price_amount_eur
          }
        } catch {
          // Silent — the admin can always run the preflight manually
        }
      }
    },
    methods: {
      async runPreflight () {
        this.preflightLoading = true
        this.preflightError = null
        this.preflightResult = null

        try {
          this.preflightResult = await createJSONRequest({ url: '/api/settings/stripe/preflight', method: 'GET' })
          if (this.preflightResult.price_amount_eur) {
            this.yearlyPrice = this.preflightResult.price_amount_eur
          }
        } catch (error) {
          this.preflightError = error?.response?.data?.error || 'Failed to run preflight check. Are you logged in as a developer?'
        } finally {
          this.preflightLoading = false
        }
      },

      async setupProduct () {
        this.productLoading = true
        this.productError = null
        this.productSuccess = null

        try {
          const yearlyPriceCents = Math.round(this.yearlyPrice * 100)
          const data = await createJSONRequest({
            url: '/api/settings/stripe/transition-reports/product',
            method: 'POST',
            data: { yearly_price_cents: yearlyPriceCents, currency: 'eur' },
          })

          this.stripe.stripe_transition_reports_product_id = data.product_id
          this.stripe.stripe_transition_reports_price_id = data.price_id

          const messages: Record<string, string> = {
            created:       'Product and price created successfully. Save settings to persist the IDs.',
            price_created: 'Price created on existing product. Save settings to persist the IDs.',
            price_updated: 'Price updated successfully. Existing subscribers keep their current price. Save settings to persist the new price ID.',
            unchanged:     'Price is unchanged — no update was needed.',
            synced:        'Product synced successfully.',
          }
          this.productSuccess = messages[data.action] ?? 'Done.'
          // Refresh preflight data so the subscriber table and price field reflect the new state
          if (data.action === 'price_updated' || data.action === 'price_created') {
            try {
              this.preflightResult = await createJSONRequest({ url: '/api/settings/stripe/preflight', method: 'GET' })
              if (this.preflightResult.price_amount_eur) {
                this.yearlyPrice = this.preflightResult.price_amount_eur
              }
            } catch { /* non-critical */ }
          }
        } catch (error) {
          this.productError = error?.response?.data?.error || 'Something went wrong while setting up the product in Stripe.'
        } finally {
          this.productLoading = false
        }
      },
    },
  }
</script>

<style scoped>
</style>
